import React, {createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode} from 'react';
import {onIdTokenChanged} from 'firebase/auth';
import {
    MonkVisit,
    MonkVisitData,
    MonkVisitImpl,
    AccommodationLocation,
    VehicleGroup,
} from '../types';
import {auth} from '../firebase/config';
import {monkPlanningApi, type PlanningResource} from '../api/adminDataApi';
import {useYearContext} from './YearContext';
import {useSyncSignal} from './SheetSyncContext';
import {hasVerifiedTotp} from '../auth/authState';

interface MonkVisitsContextType {
    monkVisits: MonkVisit[];
    accommodationLocations: AccommodationLocation[];
    vehicleGroups: VehicleGroup[];
    isLoading: boolean;
    addMonkVisit: (visit: Omit<MonkVisitData, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    updateMonkVisit: (visit: MonkVisit) => Promise<void>;
    deleteMonkVisit: (visitId: string) => Promise<void>;
    addAccommodationLocation: (location: Omit<AccommodationLocation, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    updateAccommodationLocation: (location: AccommodationLocation) => Promise<void>;
    deleteAccommodationLocation: (locationId: string) => Promise<void>;
    addVehicleGroup: (group: Omit<VehicleGroup, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    updateVehicleGroup: (group: VehicleGroup) => Promise<void>;
    deleteVehicleGroup: (groupId: string) => Promise<void>;
}

const record = (value: object): Record<string, unknown> => ({...value});

export const MonkVisitsContext = createContext<MonkVisitsContextType | undefined>(undefined);

export const MonkVisitsProvider: React.FC<{children: ReactNode}> = ({children}) => {
    const {selectedYear} = useYearContext();
    const syncSignal = useSyncSignal();
    const [monkVisits, setMonkVisits] = useState<MonkVisit[]>([]);
    const [accommodationLocations, setAccommodationLocations] = useState<AccommodationLocation[]>([]);
    const [vehicleGroups, setVehicleGroups] = useState<VehicleGroup[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const refresh = useCallback(async (authenticated = Boolean(auth.currentUser)) => {
        setIsLoading(true);
        try {
            if (!authenticated) {
                setMonkVisits([]);
                setAccommodationLocations([]);
                setVehicleGroups([]);
                return;
            }

            // Firebase reports a user as signed in before the TOTP endpoint has
            // refreshed the custom claim. Do not create a guaranteed 401 during
            // that short-lived state.
            if (!await hasVerifiedTotp(auth.currentUser)) {
                setMonkVisits([]);
                setAccommodationLocations([]);
                setVehicleGroups([]);
                return;
            }

            const response = await monkPlanningApi.list(selectedYear);
            setMonkVisits(response.visits.map(data => new MonkVisitImpl(data as unknown as MonkVisitData)));
            setAccommodationLocations(response.locations as unknown as AccommodationLocation[]);
            setVehicleGroups(response.vehicles as unknown as VehicleGroup[]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedYear]);

    useEffect(() => onIdTokenChanged(auth, user => {
        void refresh(Boolean(user)).catch(error =>
            console.error('Failed to fetch Monk planning data through the API', error));
    }), [refresh, syncSignal]);

    const mutate = useCallback(async (
        resource: PlanningResource,
        operation: 'create' | 'update' | 'remove',
        dataOrId: Record<string, unknown> | string,
    ) => {
        if (operation === 'create') {
            await monkPlanningApi.create(resource, dataOrId as Record<string, unknown>);
        } else if (operation === 'update') {
            const data = dataOrId as Record<string, unknown>;
            await monkPlanningApi.update(resource, String(data.id), data);
        } else {
            await monkPlanningApi.remove(resource, dataOrId as string);
        }
        await refresh(true);
    }, [refresh]);

    const value = useMemo<MonkVisitsContextType>(() => ({
        monkVisits,
        accommodationLocations,
        vehicleGroups,
        isLoading,
        addMonkVisit: visit => mutate('visits', 'create', {
            ...record(visit),
            year: visit.year || selectedYear,
        }),
        updateMonkVisit: visit => mutate('visits', 'update', record(visit)),
        deleteMonkVisit: id => mutate('visits', 'remove', id),
        addAccommodationLocation: location => mutate('locations', 'create', {
            ...record(location),
            year: location.year || selectedYear,
        }),
        updateAccommodationLocation: location => mutate('locations', 'update', record(location)),
        deleteAccommodationLocation: id => mutate('locations', 'remove', id),
        addVehicleGroup: group => mutate('vehicles', 'create', {
            ...record(group),
            year: group.year || selectedYear,
        }),
        updateVehicleGroup: group => mutate('vehicles', 'update', record(group)),
        deleteVehicleGroup: id => mutate('vehicles', 'remove', id),
    }), [
        monkVisits,
        accommodationLocations,
        vehicleGroups,
        isLoading,
        mutate,
        selectedYear,
    ]);

    return <MonkVisitsContext.Provider value={value}>{children}</MonkVisitsContext.Provider>;
};

export const useMonkVisits = (): MonkVisitsContextType => {
    const context = useContext(MonkVisitsContext);
    if (!context) throw new Error('useMonkVisits must be used within a MonkVisitsProvider');
    return context;
};
